import { Api, ApiResponse, DataRequestOption } from '@app/infrastructure/data-list/grid/api-grid/api';
import { IFilters } from '@app/infrastructure/data-list/grid/api-grid-filters/filters';
import { DEFAULT_GRID_STATE, Grid, ItemIdentityFn } from '@app/infrastructure/data-list/grid/grid.model';
import { IWidgetState } from '@app/infrastructure/state-switcher/state-switcher.model';
import { BehaviorSubject, EMPTY, MonoTypeOperatorFunction, Observable, pipe, ReplaySubject } from 'rxjs';
import { catchError, filter, map, scan, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

export class ApiGrid<DataType, FilterType = {}> implements Grid<DataType, FilterType> {
	protected readonly stateDispatcher = new ReplaySubject<Partial<IWidgetState>>(1);

	protected readonly totalCount$ = new BehaviorSubject<number>(0);
	protected readonly stateChange$ = this.stateDispatcher.pipe(
		startWith(DEFAULT_GRID_STATE),
		scan(
			(stored, updated) => ({
				...stored,
				...updated,
			}),
			DEFAULT_GRID_STATE,
		),
	);

	public static create<D, F>(identityFn: ItemIdentityFn<D>): (filters: IFilters<F>, api: Api<D, F>) => ApiGrid<D, F> {
		return (filters: IFilters<F>, api: Api<D, F>) => new ApiGrid<D, F>(filters, api, identityFn);
	}

	protected readonly api$: Observable<ApiResponse<DataType>> = this.filters.changes().pipe(
		tap(() => this.stateDispatcher.next({ isLoading: true })),
		switchMap((filters) => this.api.get(filters).pipe(this.addPageInfoToResponse(filters))),
		this.skipIfEmptyPage(),
		tap(({ items, totalCount }) => {
			this.totalCount$.next(totalCount);
			this.stateDispatcher.next({
				isLoading: false,
				isEmpty: items ? items.length === 0 : true,
			});
		}),
		catchError((err) => {
			this.getError(err);

			return EMPTY;
			// return of({
			// 	totalCount: 0,
			// 	pageIndex: 1,
			// 	pageSize: DEFAULT_PAGE_SIZE,
			// 	items: [],
			// });
		}),
		shareReplay(1),
	);

	constructor(
		public filters: IFilters<FilterType>,
		private api: Api<DataType, FilterType>,
		public identityFn: ItemIdentityFn<DataType>,
	) {}

	public changes(): Observable<ApiResponse<DataType>> {
		return this.api$;
	}

	public setSlice(page: number, amount: number): void {
		this.filters.navigateTo(page, amount);
	}

	public stateChanges(): Observable<IWidgetState> {
		return this.stateChange$;
	}

	public getTotalCount(): BehaviorSubject<number> | number | null {
		return this.totalCount$;
	}

	private getError(err: any): void {
		const error: any = { err };
		console.log('error: ', error);

		this.stateDispatcher.next({
			isLoading: false,
			isError: true,
		});
	}

	private skipIfEmptyPage(): MonoTypeOperatorFunction<ApiResponse<DataType>> {
		return pipe(
			filter(({ items, pageIndex }) => {
				if (pageIndex > 1 && !items.length) {
					this.filters.decreasePage();

					return false;
				}

				return true;
			}),
		);
	}

	private addPageInfoToResponse(
		filters: DataRequestOption<FilterType>,
	): MonoTypeOperatorFunction<ApiResponse<DataType>> {
		return pipe(map((response) => ({ ...response, pageIndex: Number(filters.page), pageSize: Number(filters.limit) })));
	}
}
